import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Building2, 
  Briefcase, 
  MessageSquare, 
  CheckSquare, 
  Users, 
  Settings,
  BarChart3
} from 'lucide-react';

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  href: string;
}

const menuItems: MenuItem[] = [
  { icon: <Home size={20} />, label: 'ダッシュボード', href: '/' },
  { icon: <Building2 size={20} />, label: '企業管理', href: '/accounts' },
  { icon: <Briefcase size={20} />, label: '案件管理', href: '/deals' },
  { icon: <MessageSquare size={20} />, label: '商談履歴', href: '/activities' },
  { icon: <CheckSquare size={20} />, label: 'ToDo', href: '/todos' },
  { icon: <BarChart3 size={20} />, label: 'レポート', href: '/reports' },
];

const adminMenuItems: MenuItem[] = [
  { icon: <Users size={20} />, label: 'ユーザー管理', href: '/admin/users' },
  { icon: <Settings size={20} />, label: '設定', href: '/settings' },
];

interface SidebarProps {
  isAdmin?: boolean;
}

export function Sidebar({ isAdmin = false }: SidebarProps) {
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <aside
      className="w-[170px] h-screen fixed left-0 top-0 flex flex-col z-50"
      style={{
        backgroundColor: 'var(--background-aside-primary)',
        color: 'var(--aside-text-color)',
      }}
    >
      {/* ロゴ */}
      <div className="p-4 flex items-center gap-2 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center font-bold text-lg">
          T's
        </div>
        <span className="font-bold text-sm">FUTURE CRM</span>
      </div>

      {/* メインメニュー */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200"
              style={{
                backgroundColor: isActive(item.href) 
                  ? 'var(--background-active)' 
                  : 'transparent',
                color: isActive(item.href) 
                  ? 'var(--text-primary)' 
                  : 'var(--aside-text-color)',
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.href)) {
                  e.currentTarget.style.backgroundColor = 'var(--menu-aside-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.href)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ color: isActive(item.href) ? 'var(--accent-color)' : 'var(--aside-icon)' }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </Link>
          ))}
        </div>

        {/* 管理者メニュー */}
        {isAdmin && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="px-4 text-xs text-white/50 mb-2">管理者</p>
            <div className="space-y-1">
              {adminMenuItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200"
                  style={{
                    backgroundColor: isActive(item.href) 
                      ? 'var(--background-active)' 
                      : 'transparent',
                    color: isActive(item.href) 
                      ? 'var(--text-primary)' 
                      : 'var(--aside-text-color)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive(item.href)) {
                      e.currentTarget.style.backgroundColor = 'var(--menu-aside-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.href)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ color: isActive(item.href) ? 'var(--accent-color)' : 'var(--aside-icon)' }}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* バージョン情報 */}
      <div className="p-4 text-xs text-white/30 border-t border-white/10">
        v1.0.0
      </div>
    </aside>
  );
}
